import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {enqueueSnackbar} from 'notistack'
import {BoardCard, BoardFilters, CRM_STAGES, CrmStage, LOST_STAGE_REASON, LostReason, TeamMember} from '../../domain/entities/Pipeline.ts'
import {useActivationBoard} from '../../hooks/usePipelines.ts'
import {BoardColumn} from './BoardColumn.tsx'
import {BoardFiltersBar} from './BoardFiltersBar.tsx'
import {ActivationCard} from './ActivationCard.tsx'
import {useBoardDrag} from './useBoardDrag.ts'

type ActivationBoardProps = {
    filters: BoardFilters
    onFiltersChange: (filters: BoardFilters) => void
    team: TeamMember[]
    selectedId: string | null
    onOpenCard: (card: BoardCard) => void
    onRequestLost: (card: BoardCard, reason: LostReason | null) => void
}

// Activation board: 9 derived columns. Drag and drop only closes a card into a
// Lost column (= MARK_LOST with that reason); every other drop is refused with
// "Stages update automatically" (contract §3.5).
export const ActivationBoard: React.FC<ActivationBoardProps> = ({filters, onFiltersChange, team, selectedId, onOpenCard, onRequestLost}) => {
    const {t} = useTranslation('dashboard')
    const {board, isLoadingBoard, isFetchingBoard, boardError, refetchBoard} = useActivationBoard(filters)
    const [collapsed, setCollapsed] = useState<Partial<Record<CrmStage, boolean>>>({})

    const columns = CRM_STAGES.map((stage) => board?.columns.find((c) => c.stage === stage)
        ?? {stage, actor: null, final: false, count: 0, cards: [], nextCursor: null})
    const total = columns.reduce((sum, c) => sum + c.count, 0)

    // Only the Lost columns take a card (= MARK_LOST with that reason).
    const {dragging, startDrag, endDrag, columnDragProps} = useBoardDrag<BoardCard, CrmStage>({
        stageOf: (card) => card.crmStage,
        accepts: (_, stage) => !!LOST_STAGE_REASON[stage],
        onDrop: (card, stage) => onRequestLost(card, LOST_STAGE_REASON[stage] ?? null),
        onRefused: () => enqueueSnackbar(t('pipelines.board.autoStages'), {variant: 'info'}),
    })

    return (
        <div className="flex-1 min-h-0 flex flex-col gap-3.5">
            <div className="flex items-center gap-3 flex-wrap flex-none">
                <BoardFiltersBar filters={filters} team={team} onChange={onFiltersChange}/>
                <span className="flex-1"/>
                <span className="text-[12.5px] text-neutralBlack-600" aria-live="polite">
                    {isFetchingBoard && !isLoadingBoard ? t('pipelines.board.refreshing') : t('pipelines.board.total', {count: total})}
                </span>
            </div>

            <div className="flex-1 min-h-0 flex bg-white border border-neutralBlack-200 rounded-2xl overflow-hidden">
                {boardError && !board ? (
                    <div role="alert" className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
                        <span className="text-[15px] font-semibold text-neutralBlack-900">{t('pipelines.board.errorTitle')}</span>
                        <p className="text-[13px] text-neutralBlack-600 max-w-sm">{t('pipelines.board.errorMessage')}</p>
                        <button type="button" onClick={() => refetchBoard()}
                                className="h-9 px-4 rounded-full bg-neutralBlack-900 text-white text-[13px] font-medium hover:bg-neutralBlack-700 transition-colors duration-200 ease-out">
                            {t('pipelines.retry')}
                        </button>
                    </div>
                ) : (
                    <div className="flex-1 min-w-0 flex gap-3 p-3.5 overflow-x-auto overflow-y-hidden items-stretch" aria-busy={isLoadingBoard}>
                        {isLoadingBoard
                            ? CRM_STAGES.map((stage) => (
                                <div key={stage} className="flex-none w-[300px] rounded-[14px] border border-neutralBlack-200 bg-neutralBlack-50 p-3 flex flex-col gap-2.5">
                                    <div className="h-5 w-32 rounded bg-neutralBlack-200 animate-pulse"/>
                                    {Array.from({length: 3}).map((_, i) => <div key={i} className="h-[140px] rounded-xl bg-white animate-pulse"/>)}
                                </div>
                            ))
                            : columns.map((column) => (
                                <BoardColumn<BoardCard> key={column.stage} column={column}
                                             source={{pipeline: 'activation', stage: column.stage, filters}}
                                             success={column.stage === 'REGISTERED'}
                                             dropHint={LOST_STAGE_REASON[column.stage] ? t('pipelines.column.dropHint') : null}
                                             collapsed={!!collapsed[column.stage]}
                                             onToggleCollapsed={() => setCollapsed((c) => ({...c, [column.stage]: !c[column.stage]}))}
                                             renderCard={(card) => (
                                                 <ActivationCard card={card} team={team}
                                                                 selected={card.id === selectedId} dragging={card.id === dragging?.id}
                                                                 onOpen={onOpenCard} onMarkLost={(c) => onRequestLost(c, null)}
                                                                 onDragStart={startDrag} onDragEnd={endDrag}/>
                                             )}
                                             {...columnDragProps(column.stage)}/>
                            ))}
                    </div>
                )}
            </div>
        </div>
    )
}
